import React, { useState, useEffect, useRef } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import axios from "axios";
import { v4 as uuidv4 } from "uuid"; // pour générer un id unique
import "./SignatureTemplateEditor.scss";
import { BASE_URL } from "config/config";
import { useTranslation } from "react-i18next";
pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.js`;

export default function SignatureTemplateEditor({
  fileUrl,
  recipients = [],
  signatureId,
  setShowTemplateModal,
  onSave,
}) {
  const [numPages, setNumPages] = useState(null);
  const [selectedRecipientIndex, setSelectedRecipientIndex] = useState(0);
  const [selectedFieldType, setSelectedFieldType] = useState("signature");
  const [signLocations, setSignLocations] = useState([]);
  const [draggedField, setDraggedField] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [draggedFieldId, setDraggedFieldId] = useState(null);
  const [isRepositioning, setIsRepositioning] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [zoomLevel, setZoomLevel] = useState(100);
  const [showActionsMenu, setShowActionsMenu] = useState(false);
  const [dragPreview, setDragPreview] = useState(null);
  const [cursorPosition, setCursorPosition] = useState({ x: 0, y: 0 });
  const [dropIndicator, setDropIndicator] = useState(null);
  const documentRef = useRef(null);
  const { t } = useTranslation();


  useEffect(() => {
    if (recipients.length > 0 && signLocations.length === 0) {
      setSignLocations(recipients.map(() => []));
    }
  }, [recipients]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey || e.metaKey) {
        switch (e.key) {
          case 'z':
            e.preventDefault();
            if (e.shiftKey) {
              handleRedo();
            } else {
              handleUndo();
            }
            break;
          case 'y':
            e.preventDefault();
            handleRedo();
            break;
          case 's':
            e.preventDefault();
            saveTemplate();
            break;
          case 'p':
            e.preventDefault();
            handlePrint();
            break;
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [historyIndex, history.length, recipients.length, numPages]);

  // Close actions menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showActionsMenu && !e.target.closest('.actions-dropdown')) {
        setShowActionsMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showActionsMenu]);

  // Cursor tracking for drag operations
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (isDragging) {
        setCursorPosition({ x: e.clientX, y: e.clientY });
      }
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      return () => document.removeEventListener('mousemove', handleMouseMove);
    }
  }, [isDragging]);

  // Save state to history
  const saveToHistory = (newSignLocations) => {
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push(JSON.parse(JSON.stringify(newSignLocations)));
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
  };

  // Undo function
  const handleUndo = () => {
    if (historyIndex > 0) {
      setHistoryIndex(historyIndex - 1);
      setSignLocations(JSON.parse(JSON.stringify(history[historyIndex - 1])));
    }
  };

  // Redo function
  const handleRedo = () => {
    if (historyIndex < history.length - 1) {
      setHistoryIndex(historyIndex + 1);
      setSignLocations(JSON.parse(JSON.stringify(history[historyIndex + 1])));
    }
  };

  // Zoom functions
  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(prev + 25, 300));
  };

  const handleZoomOut = () => {
    setZoomLevel(prev => Math.max(prev - 25, 50));
  };

  // Print function
  const handlePrint = () => {
    window.print();
  };

  // Actions menu toggle
  const toggleActionsMenu = () => {
    setShowActionsMenu(!showActionsMenu);
  };


  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
  };

  const handleDragStart = (e, fieldType) => {
    setDraggedField(fieldType);
    setIsDragging(true);
    setIsRepositioning(false);
    e.dataTransfer.effectAllowed = "copy";
    
    // Get the current user's color
    const userColor = getUserColor(selectedRecipientIndex);
    const recipientName = recipients[selectedRecipientIndex]?.name || 'Utilisateur';
    
    // Create custom drag preview with user's color
    const dragPreview = document.createElement('div');
    dragPreview.style.cssText = `
      position: absolute;
      top: -1000px;
      left: -1000px;
      width: ${fieldType === 'signature' ? '190px' : fieldType === 'nomPrenom' ? '150px' : fieldType === 'date' ? '200px' : fieldType === 'initial' ? '120px' : '120px'};
      height: ${fieldType === 'signature' ? '70px' : fieldType === 'nomPrenom' ? '40px' : fieldType === 'date' ? '30px' : fieldType === 'initial' ? '40px' : '40px'};
      background: ${userColor.bg};
      border: 2px solid ${userColor.border};
      border-radius: 8px;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      color: ${userColor.color};
      font-weight: bold;
      font-size: 12px;
      box-shadow: 0 8px 32px rgba(0,0,0,0.3);
      opacity: 0.9;
      z-index: 9999;
    `;
    
    const fieldInfo = fieldTypes.find(f => f.key === fieldType);
    dragPreview.innerHTML = `
      <div style="font-size: 16px; margin-bottom: 4px;">${fieldInfo?.icon || '📝'}</div>
      <div>${fieldInfo?.label || fieldType.toUpperCase()}</div>
      <div style="font-size: 10px; opacity: 0.8; margin-top: 2px;">${recipientName}</div>
    `;
    
    document.body.appendChild(dragPreview);
    e.dataTransfer.setDragImage(dragPreview, 125, 75);
    
    // Clean up after a short delay
    setTimeout(() => {
      if (document.body.contains(dragPreview)) {
        document.body.removeChild(dragPreview);
      }
    }, 0);
  };

  const handleFieldDragStart = (e, fieldId, fieldType) => {
    e.stopPropagation();
    console.log('Field drag start:', fieldId, fieldType);
    setDraggedFieldId(fieldId);
    setDraggedField(fieldType);
    setIsDragging(true);
    setIsRepositioning(true);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", fieldId);
    
    // Find the recipient index for this field
    let fieldRecipientIndex = 0;
    signLocations.forEach((locations, index) => {
      if (locations.some(field => field.id === fieldId)) {
        fieldRecipientIndex = index;
      }
    });
    
    // Get the user's color for this field
    const userColor = getUserColor(fieldRecipientIndex);
    const recipientName = recipients[fieldRecipientIndex]?.name || 'Utilisateur';
    
    // Create custom drag preview for repositioning with user's color
    const dragPreview = document.createElement('div');
    dragPreview.style.cssText = `
      position: absolute;
      top: -1000px;
      left: -1000px;
      width: ${fieldType === 'signature' ? '190px' : fieldType === 'nomPrenom' ? '150px' : fieldType === 'date' ? '200px' : fieldType === 'initial' ? '120px' : '120px'};
      height: ${fieldType === 'signature' ? '70px' : fieldType === 'nomPrenom' ? '40px' : fieldType === 'date' ? '30px' : fieldType === 'initial' ? '40px' : '40px'};
      background: ${userColor.bg};
      border: 2px solid ${userColor.border};
      border-radius: 8px;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      color: ${userColor.color};
      font-weight: bold;
      font-size: 12px;
      box-shadow: 0 8px 32px rgba(0,0,0,0.4);
      opacity: 0.9;
      z-index: 9999;
      transform: rotate(2deg);
    `;
    
    const fieldInfo = fieldTypes.find(f => f.key === fieldType);
    dragPreview.innerHTML = `
      <div style="font-size: 16px; margin-bottom: 4px;">${fieldInfo?.icon || '📝'}</div>
      <div>${fieldInfo?.label || fieldType.toUpperCase()}</div>
      <div style="font-size: 10px; opacity: 0.8; margin-top: 2px;">${recipientName} - DÉPLACER</div>
    `;
    
    document.body.appendChild(dragPreview);
    e.dataTransfer.setDragImage(dragPreview, 125, 75);
    
    // Clean up after a short delay
    setTimeout(() => {
      if (document.body.contains(dragPreview)) {
        document.body.removeChild(dragPreview);
      }
    }, 0);
  };

  const handleDragEnd = () => {
    setDraggedField(null);
    setDraggedFieldId(null);
    setIsDragging(false);
    setIsRepositioning(false);
    setDropIndicator(null);
  };

  const handleFieldDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isRepositioning) {
      e.dataTransfer.dropEffect = "move";
    } else {
      e.dataTransfer.dropEffect = "copy";
    }
    
    // Show drop indicator
    if (isDragging) {
      const rect = e.currentTarget.getBoundingClientRect();
      const scrollTop = e.currentTarget.scrollTop || 0;
      const scrollLeft = e.currentTarget.scrollLeft || 0;
      const x = e.clientX - rect.left + scrollLeft;
      const y = e.clientY - rect.top + scrollTop;
      const adjustedX = x / (zoomLevel / 100);
      const adjustedY = y / (zoomLevel / 100);
      
      setDropIndicator({
        x: adjustedX,
        y: adjustedY,
        type: draggedField
      });
    }
  };

  const handleDrop = (e, pageIndex) => {
    e.preventDefault();
    e.stopPropagation();
    
    console.log('Drop event:', { draggedField, draggedFieldId, isRepositioning, pageIndex });
    
    if (!draggedField || !recipients[selectedRecipientIndex]) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const scrollTop = e.currentTarget.scrollTop || 0;
    const scrollLeft = e.currentTarget.scrollLeft || 0;
    
    // More precise positioning using cursor coordinates
    const x = e.clientX - rect.left + scrollLeft;
    const y = e.clientY - rect.top + scrollTop;
    
    // Adjust for zoom level
    const adjustedX = x / (zoomLevel / 100);
    const adjustedY = y / (zoomLevel / 100);

    if (isRepositioning && draggedFieldId) {
      console.log('Repositioning field:', draggedFieldId, 'to position:', adjustedX, adjustedY);
      // Repositioning existing field
      setSignLocations((prev) => {
        const newLocations = [...prev];
        newLocations[selectedRecipientIndex] = newLocations[selectedRecipientIndex].map((field) => {
          if (field.id === draggedFieldId) {
            return {
              ...field,
              page: pageIndex + 1,
              x: adjustedX,
              y: adjustedY,
            };
          }
          return field;
        });
        saveToHistory(newLocations);
        return newLocations;
      });
    } else {
      // Creating new field
      // Définir width et height selon le type sélectionné
      let width = 120;
      let height = 50;

      if (draggedField === "signature") {
        width = 190;
        height = 70;
      } else if (draggedField === "nomPrenom") {
        width = 150;
        height = 40;
      } else if (draggedField === "date") {
        width = 200;
        height = 30;
      } else if (draggedField === "initial") {
        width = 120;
        height = 40;
      }

      setSignLocations((prev) => {
        const newLocations = [...prev];
        const fieldName = recipients[selectedRecipientIndex]?.name || 'Sans nom';
        console.log('Creating field with name:', fieldName, 'for recipient:', recipients[selectedRecipientIndex]);
        newLocations[selectedRecipientIndex] = [
          ...(newLocations[selectedRecipientIndex] || []),
          {
            id: uuidv4(),
            page: pageIndex + 1,
            x: adjustedX,
            y: adjustedY,
            width,
            height,
            name: fieldName,
            type: draggedField,
          },
        ];
        saveToHistory(newLocations);
        return newLocations;
      });
    }

    setDraggedField(null);
    setDraggedFieldId(null);
    setIsDragging(false);
    setIsRepositioning(false);
  };

  const handlePageClick = (pageIndex, event) => {
    if (!recipients[selectedRecipientIndex]) return;

    const rect = event.currentTarget.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    // Définir width et height selon le type sélectionné
    let width = 120;
    let height = 50;

    if (selectedFieldType === "signature") {
      width = 190;
      height = 70;
    } else if (selectedFieldType === "nomPrenom") {
      width = 150;
      height = 40;
    } else if (selectedFieldType === "date") {
      width = 200;
      height = 30;
    }

    setSignLocations((prev) => {
      const newLocations = [...prev];
      newLocations[selectedRecipientIndex] = [
        ...(newLocations[selectedRecipientIndex] || []),
        {
          id: uuidv4(),
          page: pageIndex + 1,
          x,
          y,
          width,
          height,
          name: recipients[selectedRecipientIndex].name,
          type: selectedFieldType,
        },
      ];
      return newLocations;
    });
  };

  const removeField = (recipientIndex, fieldId) => {
    setSignLocations((prev) => {
      const newLocations = [...prev];
      newLocations[recipientIndex] = newLocations[recipientIndex].filter(
        (pos) => pos.id !== fieldId
      );
      saveToHistory(newLocations);
      return newLocations;
    });
  };

  const saveTemplate = async () => {
    if (!signatureId) return;

    const payload = {
      signatureFields: recipients.map((r, index) => ({
        recipientId: r._id,
        positions: signLocations[index] || [],
      })),
    };

    try {
      await axios.patch(
        `${BASE_URL}signatures/${signatureId}/signature-fields`,
        payload
      );
      setShowTemplateModal(false);
      onSave(true);
      alert(t("signature.emplSave"));
    } catch (err) {
      console.error(err);
      alert(t("signature.erreurSave"));
    }
  };

  // Generate unique colors for each user
  const getUserColor = (recipientIndex) => {
    const colors = [
      { border: "#1B10B1", bg: "#E5E6FC", color: "#1B10B1" }, // Blue
      { border: "#2B552B", bg: "#BEDEBE", color: "#2B552B" }, // Green
      { border: "#9E7A1B", bg: "#FFF3D4", color: "#9E7A1B" }, // Orange
      { border: "#8B1A8B", bg: "#E6CCE6", color: "#8B1A8B" }, // Purple
      { border: "#DC143C", bg: "#FFE4E1", color: "#DC143C" }, // Crimson
      { border: "#006400", bg: "#F0FFF0", color: "#006400" }, // Dark Green
      { border: "#FF6347", bg: "#FFF5EE", color: "#FF6347" }, // Tomato
      { border: "#4169E1", bg: "#F0F8FF", color: "#4169E1" }, // Royal Blue
      { border: "#8B4513", bg: "#F5DEB3", color: "#8B4513" }, // Saddle Brown
      { border: "#2F4F4F", bg: "#F0FFFF", color: "#2F4F4F" }, // Dark Slate Gray
    ];
    
    return colors[recipientIndex % colors.length];
  };

  const getColorByType = (type, recipientIndex) => {
    const userColor = getUserColor(recipientIndex);
    
    switch (type) {
      case "signature":
        return {
          border: `2px solid ${userColor.border}`,
          bg: userColor.bg,
          color: userColor.color,
        };
      case "nomPrenom":
        return {
          border: `2px solid ${userColor.border}`,
          bg: userColor.bg,
          color: userColor.color,
        };
      case "initial":
        return {
          border: `2px solid ${userColor.border}`,
          bg: userColor.bg,
          color: userColor.color,
        };
      case "date":
        return {
          border: `2px solid ${userColor.border}`,
          bg: userColor.bg,
          color: userColor.color,
        };
      default:
        return {
          border: `2px solid ${userColor.border}`,
          bg: userColor.bg,
          color: userColor.color,
        };
    }
  };

  const fieldTypes = [
    { 
      key: "signature", 
      label: "Signature", 
      icon: "✍️",
      category: "Champs standards"
    },
    { 
      key: "nomPrenom", 
      label: "Nom et Prénom", 
      icon: "👤",
      category: "Champs standards"
    },
    { 
      key: "initial", 
      label: "Initial", 
      icon: "🔤",
      category: "Champs standards"
    },
    { 
      key: "date", 
      label: "Date de signature", 
      icon: "📅",
      category: "Champs standards"
    },
  ];

  return (
    <div className="signature-template-editor">

      {/* Top Bar */}
      <div className="top-bar">
        <div className="top-controls">
          <button 
            className="control-btn" 
            onClick={handleUndo}
            disabled={historyIndex <= 0}
            title="Annuler (Ctrl+Z)"
          >
            ↶
          </button>
          <button 
            className="control-btn" 
            onClick={handleRedo}
            disabled={historyIndex >= history.length - 1}
            title="Rétablir (Ctrl+Y)"
          >
            ↷
          </button>
          <button 
            className="control-btn" 
            onClick={saveTemplate}
            disabled={recipients.length === 0 || numPages === null}
            title="Enregistrer (Ctrl+S)"
          >
            💾
          </button>
          <button 
            className="control-btn" 
            onClick={handlePrint}
            title="Imprimer (Ctrl+P)"
          >
            🖨️
          </button>
        </div>
        <div className="zoom-control">
          <button 
            className="control-btn" 
            onClick={handleZoomOut}
            title="Zoom arrière"
          >
            −
          </button>
          <span>{zoomLevel}%</span>
          <button 
            className="control-btn" 
            onClick={handleZoomIn}
            title="Zoom avant"
          >
            +
          </button>
        </div>
        <div className="top-actions">
          <button 
            className="control-btn" 
            title="Nouvelle page"
          >
            📄+
          </button>
          <button 
            className="control-btn" 
            title="Aide"
          >
            ❓
          </button>
          <div className="actions-dropdown">
            <button 
              className="control-btn" 
              onClick={toggleActionsMenu}
              title="Actions"
            >
              Actions
            </button>
            {showActionsMenu && (
              <div className="actions-menu">
                <button onClick={() => { setShowActionsMenu(false); }}>Exporter PDF</button>
                <button onClick={() => { setShowActionsMenu(false); }}>Dupliquer</button>
                <button onClick={() => { setShowActionsMenu(false); }}>Renommer</button>
                <button onClick={() => { setShowActionsMenu(false); }}>Supprimer</button>
              </div>
            )}
          </div>
          <button 
            className="control-btn" 
            title="Approuver"
          >
            A
          </button>
        </div>
      </div>

      <div className="editor-container">
        {/* Left Sidebar */}
        <div className="left-sidebar">
          <div className="recipient-selector">
            <select
              value={selectedRecipientIndex}
              onChange={(e) => setSelectedRecipientIndex(Number(e.target.value))}
              disabled={recipients.length === 0}
              className="recipient-dropdown">
              {recipients.map((r, i) => {
                const userColor = getUserColor(i);
                return (
                  <option key={r._id} value={i} style={{ color: userColor.color }}>
                    {r.name}
                  </option>
                );
              })}
            </select>
            
            {/* Color legend for recipients */}
            <div className="color-legend">
              <h4 style={{ fontSize: 12, margin: "8px 0 4px 0", color: "#666" }}>Couleurs des utilisateurs:</h4>
              {recipients.map((r, i) => {
                const userColor = getUserColor(i);
                return (
                  <div key={r._id} style={{ 
                    display: "flex", 
                    alignItems: "center", 
                    marginBottom: 2,
                    fontSize: 10 
                  }}>
                    <div style={{
                      width: 12,
                      height: 12,
                      backgroundColor: userColor.border,
                      borderRadius: "50%",
                      marginRight: 6,
                      border: "1px solid #ccc"
                    }} />
                    <span style={{ color: userColor.color, fontWeight: "bold" }}>
                      {r.name}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="search-bar">
            <input 
              type="text" 
              placeholder="Champs de recher..." 
              className="field-search"
            />
          </div>

          <div className="field-categories">
            <h4 className="category-title">Champs standards</h4>
            <div className="field-list">
              {fieldTypes.map((field) => (
                <div
                  key={field.key}
                  className={`field-item ${isDragging && draggedField === field.key ? 'dragging' : ''}`}
                  draggable
                  onDragStart={(e) => handleDragStart(e, field.key)}
                  onDragEnd={handleDragEnd}
                >
                  <span className="field-icon">{field.icon}</span>
                  <span className="field-label">{field.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Main Document Area */}
        <div className="document-area" ref={documentRef}>
          <Document
            file={fileUrl}
            onLoadSuccess={onDocumentLoadSuccess}
            loading="Chargement du PDF..."
            renderAnnotationLayer={false}
            renderTextLayer={false}>
            {Array.from(new Array(numPages), (_, index) => (
              <div 
                key={index} 
                className="page-container"
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, index)}
              >
                <Page
                  pageNumber={index + 1}
                  width={Math.min(documentRef.current?.clientWidth || 800, 1200) * (zoomLevel / 100)}
                  renderAnnotationLayer={false}
                  renderTextLayer={false}
                />

                {/* Drop Indicator */}
                {dropIndicator && (
                  <div
                    className="drop-indicator"
                    style={{
                      position: "absolute",
                      left: dropIndicator.x,
                      top: dropIndicator.y,
                      width: dropIndicator.type === 'signature' ? 190 : dropIndicator.type === 'nomPrenom' ? 150 : dropIndicator.type === 'date' ? 200 : dropIndicator.type === 'initial' ? 120 : 120,
                      height: dropIndicator.type === 'signature' ? 70 : dropIndicator.type === 'nomPrenom' ? 40 : dropIndicator.type === 'date' ? 30 : dropIndicator.type === 'initial' ? 40 : 40,
                      border: "2px dashed #007bff",
                      backgroundColor: "rgba(0, 123, 255, 0.1)",
                      borderRadius: "8px",
                      pointerEvents: "none",
                      zIndex: 1000,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#007bff",
                      fontWeight: "bold",
                      fontSize: "12px",
                      boxShadow: "0 4px 16px rgba(0, 123, 255, 0.3)",
                    }}>
                    <div style={{ textAlign: "center" }}>
                      <div style={{ fontSize: "16px", marginBottom: "4px" }}>
                        {fieldTypes.find(f => f.key === dropIndicator.type)?.icon || '📝'}
                      </div>
                      <div>DÉPOSER ICI</div>
                    </div>
                  </div>
                )}

                {signLocations.map((locations, recipientIndex) =>
                  locations
                    ?.filter((pos) => pos.page === index + 1)
                    .map((pos) => {
                      const colors = getColorByType(pos.type, recipientIndex);
                      const userColor = getUserColor(recipientIndex);
                      console.log('Rendering field:', pos.type, 'with name:', pos.name, 'for recipient:', recipientIndex, 'with color:', userColor);
                      return (
                        <div
                          key={pos.id}
                          className={`field-overlay ${isDragging && draggedFieldId === pos.id ? 'dragging' : ''}`}
                          draggable
                          onDragStart={(e) => handleFieldDragStart(e, pos.id, pos.type)}
                          onDragEnd={handleDragEnd}
                          style={{
                            position: "absolute",
                            border: colors.border,
                            backgroundColor: colors.bg,
                            left: pos.x,
                            top: pos.y,
                            width: pos.width,
                            height: pos.height,
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            alignItems: "center",
                            fontWeight: "bold",
                            color: colors.color,
                            fontSize: 10,
                            textAlign: "center",
                            borderRadius: "8px",
                            boxShadow: `0 2px 8px ${userColor.border}40`,
                            transition: "all 0.2s ease",
                            cursor: "move",
                          }}>
                          <div style={{ fontSize: 12, marginBottom: 2 }}>{pos.type.toUpperCase()}</div>
                          <div style={{ fontSize: 9, fontWeight: "normal" }}>{pos.name}</div>
                          
                          {/* User color indicator */}
                          <div 
                            style={{
                              position: "absolute",
                              top: 2,
                              left: 2,
                              width: 8,
                              height: 8,
                              backgroundColor: userColor.border,
                              borderRadius: "50%",
                              border: "1px solid white",
                            }}
                          />

                          {/* bouton supprimer champ */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation(); // éviter d'ajouter un champ
                              removeField(recipientIndex, pos.id);
                            }}
                            style={{
                              position: "absolute",
                              top: -15,
                              right: -15,
                              background: "red",
                              color: "white",
                              border: "none",
                              borderRadius: "50%",
                              width: 20,
                              height: 20,
                              cursor: "pointer",
                              fontSize: 12,
                              boxShadow: "0 2px 4px rgba(0,0,0,0.3)",
                            }}>
                            X
                          </button>
                        </div>
                      );
                    })
                )}
              </div>
            ))}
          </Document>
        </div>
      </div>

      {/* Save Button */}
      <div className="save-section">
        <button
          onClick={saveTemplate}
          className="save-btn"
          disabled={recipients.length === 0 || numPages === null}>
          {t("signature.enregiestrer")}
        </button>
      </div>
    </div>
  );
}
